import i18n from '@/translations/i18n'
import {z} from 'zod'
import {
    AslDistrict,
    City,
    Contact,
    Country,
    MaritalStatus,
    PatientAgeRange,
    Province,
    UploadedDocument,
    WorkSector
} from '@/types.ts'
import {isValidPhoneNumber} from 'react-phone-number-input'
import {AuthUserPermissions} from '../auth/types'

export const Attachment = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    url: z.string().url()
})

export const ContractType = z.enum(['consultant', 'flat_rate', 'employee'])
export type ContractType = z.infer<typeof ContractType>

export const CogeapsCertification = z.enum(['certifiable', 'excluded', 'not_certifiable'])
export type CogeapsCertification = z.infer<typeof CogeapsCertification>

export const Period = z.object({
    label: z.string(),
    startDate: z.date(),
    endDate: z.date()
})
export type Period = z.infer<typeof Period>

export const EcmScore = z.object({
    startDate: z.string(),
    endDate: z.string(),
    score: z.number(),
    cogeapsCertification: CogeapsCertification.nullable()
})
export type EcmScore = z.infer<typeof EcmScore>

export const UserQualificationType = z.enum(['bachelor_degree', 'master_degree', 'doctorate', 'specialization'], {
    required_error: i18n.t('errors:required')
})
export type UserQualificationType = z.infer<typeof UserQualificationType>

export const UserQualification = z.object({
    id: z.string().min(1),
    type: UserQualificationType,
    institute: z.string(),
    graduationYear: z.number(),
    otherInformation: z.string().nullable(),
    sector: z.string()
})

export const UserWorkArea = z.object({
    id: z.string(),
    areaType: z.enum(['neighborhood', 'city']),
    area: z.object({
        name: z.string(),
        provinceAcronym: z.string().optional()
    })
})
export type UserWorkArea = z.infer<typeof UserWorkArea>

export const UserAddress = z.object({
    formatted_address: z.string(),
    address_components: z
        .object({
            long_name: z.string(),
            short_name: z.string(),
            types: z.string().array()
        })
        .array()
})
export type UserAddress = z.infer<typeof UserAddress>

export const UserSpecialization = z.object({
    id: z.string(),
    name: z.string()
})
export type UserSpecialization = z.infer<typeof UserSpecialization>

export const PermissionKey = z.enum([
    'can_view_projects',
    'can_view_patients',
    'can_write_users',
    'can_write_treatment_requests',
    'can_write_patients',
    'can_view_settings',
    'can_write_messages',
    'can_view_users',
    'can_write_agenda',
    'can_view_agenda',
    'can_write_accesses',
    'can_write_medical_records',
    'can_view_treatment_requests',
    'can_view_messages',
    'can_view_accesses',
    'can_write_settings',
    'can_write_projects',
    'can_view_medical_records',
    'can_view_user_details',
    'can_write_user_details',
    'can_write_user_details',
    'can_view_event_status',
    'can_write_event_status',
    'can_send_mail'
])
export type PermissionKey = z.infer<typeof PermissionKey>

export const Permission = z.object({
    id: z.string(),
    name: PermissionKey
})
export type Permission = z.infer<typeof Permission>

export const Role = z.object({
    id: z.string(),
    name: z.string(),
    defaultPermissions: Permission.array()
})
export type Role = z.infer<typeof Role>

export const UserDetailsSearchParams = z.object({
    sidebarSection: z.enum(['comments', 'activities']).default('activities')
})

export const InterruptionReason = z.enum([
    'health_reasons',
    'personal_reasons',
    'unavailability',
    'transfer',
    'incompatibility',
    'retirement'
])

export const UserBase = z.object({
    avatarUrl: z.string().url().nullable(),
    birthDate: z.string().min(1),
    canBeDeleted: z.boolean(),
    createdAt: z.string().min(1),
    curriculumLastUpdate: z.string().min(1).nullable(),
    fieldsToCompile: z.string().array(),
    fieldsToUpdate: z.string().array(),
    registrationDate: z.string().min(1).nullable(),
    professionalInsuranceLastVerify: z.string().min(1).nullable(),
    absenceOfIncompatibilityLastVerify: z.number().nullable(),
    qualificationsLastUpdate: z.string().min(1).nullable(),
    workSuitableUpTo: z.string().min(1).nullable(),
    weeklyHours: z.number().nullable(),
    privacyYear: z.number().nullable(),
    professionalBoardRegistration: z.coerce.boolean(),
    privacyB: z.coerce.boolean(),
    privacyC: z.coerce.boolean(),
    email: z.string().trim().email(),
    fullName: z.string().min(1),
    id: z.string().min(1),
    lastLoginAt: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    pec: z.string().email().nullable().optional(),
    patientAgeRange: z.enum(['evolutionary_age', 'evolutionary_age_adults', 'adults']).nullable(),
    status1: z.enum(['operative', 'absent', 'suspended', 'terminated', 'candidate']),
    workSector: WorkSector.nullable()
})
export type UserBase = z.infer<typeof UserBase>

export const UserSmall = UserBase.merge(
    z.object({
        roleName: z.string().min(1).nullable(),
        specializationName: z.string().min(1),
        specializationId: z.string().min(1),
        // Marks the fake users that represent an external structure (e.g. "Villa Giuseppina").
        // Optional because the backend does not expose it on the `micro` response type.
        isExternalStructure: z.boolean().optional()
    })
)
export type UserSmall = z.infer<typeof UserSmall>

/* Row of the operative therapists directory (`POST /users/operative-index`): a `UserSmall` enriched
with the user's work areas. Zod strips the extra backend fields on each work area. */
export const OperativeUser = UserSmall.extend({workAreas: z.array(UserWorkArea)})
export type OperativeUser = z.infer<typeof OperativeUser>

export const UserExtended = UserBase.merge(
    z.object({
        birthCity: z
            .object({
                id: z.string(),
                name: z.string().min(1),
                provinceAcronym: z.string().min(1)
            })
            .nullable(),
        birthCountry: z.object({
            id: z.string().min(1),
            name: z.string().min(1)
        }),
        candidatureDate: z.string().min(1).nullable(),
        contractType: ContractType.nullable(),
        amount: z.number().nonnegative().nullable(),
        curriculum: z
            .object({
                id: z.string().min(1),
                name: z.string().min(1),
                url: z.string().url()
            })
            .nullable(),
        domicile: z.object({
            address: z.string().min(1),
            cap: z.string().min(1),
            city: z.object({
                name: z.string().min(1),
                latitude: z.string().min(1),
                longitude: z.string().min(1),
                provinceAcronym: z.string().min(1)
            }),
            id: z.string().min(1),
            streetNumber: z.string().min(1)
        }),
        domicileAslDistrict: z
            .object({
                id: z.string().min(1),
                name: z.string().min(1),
                aslId: z.string().min(1),
                aslName: z.string().min(1)
            })
            .nullable(),
        ecmScore: EcmScore.nullable(),
        email: z.string().email(),
        firstName: z.string().min(1),
        fm07Evaluation: z.number().nullable(),
        fm07EvaluationAttachment: Attachment.nullable(),
        fm08Evaluation: z.number().nullable(),
        fm08EvaluationAttachment: Attachment.nullable(),
        fm08EvaluationLastUpdate: z.string().nullable(),
        fm68: z.coerce.boolean(),
        gender: z.enum(['male', 'female']).nullable(),
        iban: z.string().nullable(),
        interruptionDate: z.string().min(1).nullable(),
        interruptionReason: InterruptionReason.nullable(),
        lastName: z.string().min(1),
        maritalStatus: MaritalStatus.nullable(),
        patientAgeRange: PatientAgeRange.nullable(),
        paymentMethod: z.enum(['bank_transfer']).nullable(),
        phoneNumber: z.string(),
        qualifications: UserQualification.array().nullable(),
        permissions: AuthUserPermissions.nullable(),
        permissionsOverwritten: z.boolean(),
        raExempt: z.boolean(),
        registrationDate: z.string().min(1).nullable(),
        registrationLocation: z.string().min(1).nullable(),
        registrationNumber: z.string().min(1).nullable(),
        residence: z.object({
            address: z.string().min(1),
            cap: z.string().min(1),
            city: z.object({
                name: z.string().min(1),
                latitude: z.string().min(1),
                longitude: z.string().min(1),
                provinceAcronym: z.string().min(1)
            }),
            id: z.string().min(1),
            streetNumber: z.string().min(1)
        }),
        residenceAslDistrict: z
            .object({
                id: z.string().min(1),
                name: z.string().min(1),
                aslId: z.string().min(1),
                aslName: z.string().min(1)
            })
            .nullable(),
        role: Role.nullable(),
        specialization: z.object({
            id: z.string().min(1),
            name: z.string().min(1)
        }),
        startDate: z.string().min(1).nullable(),
        suspensionEndDate: z.string().min(1).nullable(),
        suspensionStartDate: z.string().min(1).nullable(),
        suspensionReason: z.string().nullable(),
        taxCode: z.string().min(1),
        temporaryAbsenceEndDate: z.string().min(1).nullable(),
        temporaryAbsenceStartDate: z.string().min(1).nullable(),
        vatNumber: z.string().min(1).nullable(),
        workAreas: z.array(UserWorkArea).nullable(),
        workSuitableUpTo: z.string().nullable(),
        contacts: z.array(Contact).nullish()
    })
)
export type UserExtended = z.infer<typeof UserExtended>

export const UserByPatient = UserBase.pick({id: true, fullName: true})
export type UserByPatient = z.infer<typeof UserByPatient>

/* User base form schema:
This schema is used for validating the basic personal information of a user, such as their name, email, phone number, gender, birth date, tax code, and marital status. 
These fields are essential for identifying the user and ensuring the correctness of their personal information. */
export const UserBaseInfoFormValidationSchema = z.object({
    lastName: z
        .string()
        .min(1, i18n.t('errors:required'))
        .trim()
        .regex(/^[a-zA-Zàèìòùáéíóúäëïöüâêîôûñçß\s'-]+$/i, {message: i18n.t('errors:characters')}),
    firstName: z
        .string()
        .min(1, i18n.t('errors:required'))
        .trim()
        .regex(/^[a-zA-Zàèìòùáéíóúäëïöüâêîôûñçß\s'-]+$/i, {message: i18n.t('errors:characters')}),
    email: z
        .string()
        .email({message: i18n.t('errors:email_error')})
        .transform(val => val.toLowerCase().trim()),
    phoneNumber: z.string({required_error: i18n.t('errors:required')}).refine(val => isValidPhoneNumber(val), {
        message: i18n.t('errors:phone_number')
    }),
    gender: z.string({invalid_type_error: i18n.t('errors:required')}).min(4, i18n.t('errors:required')),
    birthDate: z.date({
        errorMap: issue => ({
            message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
        })
    }),
    taxCode: z
        .string()
        .min(1, i18n.t('errors:required'))
        .toUpperCase()
        .trim()
        .regex(/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/, {message: i18n.t('errors:invalid_tax_code')}),
    maritalStatus: z.string().nullish(),
    pec: z
        .string()
        .email({message: i18n.t('errors:email_error')})
        .or(z.literal(''))
        .nullish()
})

/* User location form schema:
This schema validates the user's place of birth, including whether they were born abroad or not, and details such as province, country, and city.
Conditional validations are used to ensure correct dependencies between these fields. */
export const UserLocationInfoFormValidationSchema = z
    .object({
        isBornAbroad: z.boolean().default(false),
        birthProvince: Province.nullish(),
        birthCountry: Country.nullish(),
        birthCity: z.object(City.shape).nullish()
    })
    .refine(values => values.isBornAbroad || (!values.isBornAbroad && values.birthCity), {
        message: i18n.t('errors:required'),
        path: ['birthCity']
    })
    .refine(values => !values.isBornAbroad || (values.isBornAbroad && values.birthCountry), {
        message: i18n.t('errors:required'),
        path: ['birthCountry']
    })
    .refine(values => values.isBornAbroad || (!values.isBornAbroad && values.birthProvince), {
        message: i18n.t('errors:required'),
        path: ['birthProvince']
    })

/* User contacts form schema:
This schema validates the optional additional contacts (mobile phone, telephone, email) associated with a user.
Each entry requires a label, and contact/type are validated according to the type (phone format for phone, email format for email). */
export const UserContactsSchema = z.array(
    z
        .object({
            id: z.string().min(1).optional(),
            label: z
                .string({required_error: i18n.t('errors:missing_data')})
                .min(1, i18n.t('errors:missing_data'))
                .max(100)
        })
        .and(
            z
                .object({
                    type: z.enum(['mobile_phone', 'telephone']),
                    contact: z
                        .string({required_error: i18n.t('errors:missing_data')})
                        .min(1, {message: i18n.t('errors:missing_data')})
                        .refine(value => isValidPhoneNumber(value), {
                            message: i18n.t('errors:phone_number')
                        })
                })
                .or(
                    z.object({
                        type: z.literal('email'),
                        contact: z
                            .string({required_error: i18n.t('errors:missing_data')})
                            .email({message: i18n.t('errors:email_error')})
                            .transform(val => val.toLowerCase().trim())
                    })
                )
        )
)
export type UserContactsSchema = z.infer<typeof UserContactsSchema>

export const UserContactsFormValidationSchema = z.object({
    contacts: UserContactsSchema.optional().default([])
})
export type UserContactsFormValidationSchema = z.infer<typeof UserContactsFormValidationSchema>

/* User personal data form schema:
This schema combines user base info and location info to provide a comprehensive validation for all personal user data. */
export const UserPersonalDataFormValidationSchema = UserBaseInfoFormValidationSchema.and(
    UserLocationInfoFormValidationSchema
).and(UserContactsFormValidationSchema)
export type UserPersonalDataFormValidationSchema = z.infer<typeof UserPersonalDataFormValidationSchema>

/* User residence form schema:
This schema is used to validate the user's residence details, including address, street number, city, province, postal code, and the related ASL. */
export const UserResidenceFormValidationSchema = z.object({
    residence: z.object(
        {
            address: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required')),
            streetNumber: z.string().nullish(),
            city: z
                .object(City.omit({id: true, longitude: true, latitude: true}).shape, {
                    required_error: i18n.t('errors:required')
                })
                .nullable(),
            province: z.object(Province.shape, {required_error: i18n.t('errors:required')}).nullable(),
            postalCode: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required'))
        },
        {required_error: i18n.t('errors:required'), invalid_type_error: i18n.t('errors:required')}
    ),
    residenceAslDistrict: z.object(AslDistrict.shape, {required_error: i18n.t('errors:required')}).nullable()
})
export type UserResidenceFormValidationSchema = z.infer<typeof UserResidenceFormValidationSchema>

/* User residence manual form schema:
This schema extends the user residence schema for manual compiling and adds custom refinement to validate city and province fields if they are not present. */
export const UserResidenceManualFormModalValidationSchema = UserResidenceFormValidationSchema.superRefine(
    (values, context) => {
        if (!values.residence.city) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['residence.city']
            })
        }
        if (!values.residence.province) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['residence.province']
            })
        }
    }
)
export type UserResidenceManualFormModalValidationSchema = z.infer<typeof UserResidenceManualFormModalValidationSchema>

/* User domicile base form schema:
This schema validates the user's domicile base information, differentiating between cases where the domicile is the same or different from the residence. */
export const UserDomicileFormBaseSchema = z.object({
    isDifferentDomicile: z.boolean(),
    domicile: z
        .object({
            address: z.string().optional(),
            streetNumber: z.string().optional(),
            city: z.object(City.omit({id: true, longitude: true, latitude: true}).shape).nullish(),
            province: z.object(Province.shape, {required_error: i18n.t('errors:required')}).nullish(),
            postalCode: z.string().nullish()
        })
        .nullish(),
    domicileAslDistrict: z.object(AslDistrict.shape, {required_error: i18n.t('errors:required')}).nullish()
})
export type UserDomicileFormBaseSchema = z.infer<typeof UserDomicileFormBaseSchema>

/* User domicile form validation schema:
Extends the base domicile schema to add specific validation rules based on whether the domicile differs from the residence. */
export const UserDomicileFormValidationSchema = UserDomicileFormBaseSchema.refine(
    values => !values.isDifferentDomicile || (values.isDifferentDomicile && values?.domicile?.address),
    {
        message: i18n.t('errors:required'),
        path: ['domicile.address']
    }
)
    .refine(values => !values.isDifferentDomicile || (values.isDifferentDomicile && values?.domicile?.city), {
        message: i18n.t('errors:required'),
        path: ['domicile.city']
    })
    .refine(values => !values.isDifferentDomicile || (values.isDifferentDomicile && values?.domicile?.province), {
        message: i18n.t('errors:required'),
        path: ['domicile.province']
    })
    .refine(values => !values.isDifferentDomicile || (values.isDifferentDomicile && values?.domicile?.postalCode), {
        message: i18n.t('errors:required'),
        path: ['domicile.postalCode']
    })
export type UserDomicileFormValidationSchema = z.infer<typeof UserDomicileFormValidationSchema>

/* User domicile manual form schema:
Extends the domicile form schema to manually validate city and province fields if not provided. */
export const UserDomicileManualFormModalValidationSchema = UserDomicileFormBaseSchema.superRefine((values, context) => {
    if (!values.domicile?.city) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('errors:required'),
            path: ['domicile.city']
        })
    }
    if (!values.domicile?.province) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('errors:required'),
            path: ['domicile.province']
        })
    }
})
export type UserDomicileManualFormModalValidationSchema = z.infer<typeof UserDomicileManualFormModalValidationSchema>

/* User work areas form schema:
This schema validates the work areas of the user, including specialization, work sector, and work areas.
Specific validation rules are applied depending on the specialization. */
export const UserWorkAreasFormSchema = z.object({
    specialization: z.object(UserSpecialization.shape, {required_error: i18n.t('errors:required')}),
    workSector: z.string().nullish(),
    workAreas: z.object(UserWorkArea.shape).array().nullish()
})
export type UserWorkAreasFormSchema = z.infer<typeof UserWorkAreasFormSchema>

export const UserWorkAreasFormValidationSchema = UserWorkAreasFormSchema.refine(
    values =>
        values.specialization.name == 'Amministrativo' ||
        (values.specialization && values.specialization.name != 'Amministrativo' && values.workSector),
    {
        message: i18n.t('errors:required'),
        path: ['workSector']
    }
).refine(
    values =>
        !values.workSector ||
        values.workSector == 'outpatient' ||
        (values.workSector && values.workAreas && values?.workAreas?.length > 0),
    {
        message: i18n.t('errors:required'),
        path: ['workAreas']
    }
)

export type UserWorkAreasFormValidationSchema = z.infer<typeof UserWorkAreasFormValidationSchema>

/* User qualifications form schema:
This schema validates the educational qualifications of the user, including institute, sector, and graduation year. */
export const UserQualificationFormValidationSchema = z.object({
    type: UserQualificationType,
    institute: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required')),
    sector: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required')),
    graduationYear: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required')),
    otherInformation: z.string().nullish()
})
export type UserQualificationFormValidationSchema = z.infer<typeof UserQualificationFormValidationSchema>

/* User ECM scores form schema:
This schema is used to validate the user's ECM scores, allowing for up to three separate score entries. */
export const UserProfessionalEcmScoreValidationSchema = z.object({
    ecmScore: z
        .object({
            period: Period.nullish(),
            score: z.number().nullish(),
            cogeapsCertification: CogeapsCertification.nullish()
        })
        .nullish()
})
export type UserProfessionalEcmScoreValidationSchema = z.infer<typeof UserProfessionalEcmScoreValidationSchema>

/* User professional register form schema:
This schema validates the user's professional registration details, such as registration number, location, and date. */
export const UserProfessionalRegisterFormValidationSchema = z.object({
    registrationNumber: z.string().nullish(),
    registrationLocation: z.string().nullish(),
    registrationDate: z.date().nullish()
})
export type UserProfessionalRegisterFormValidationSchema = z.infer<typeof UserProfessionalRegisterFormValidationSchema>

/* User curriculum form schema:
This schema is used to validate the user's curriculum, including the last update date and the attachment file. */
export const UserCurriculumFormValidationSchema = z.object({
    curriculumLastUpdate: z.date().nullish(),
    curriculumAttachment: UploadedDocument.nullish()
})
export type UserCurriculumFormValidationSchema = z.infer<typeof UserCurriculumFormValidationSchema>

/* User professional insurance form schema:
Validates the user's professional insurance information, specifically the last verification date. */
export const UserProfessionalInsuranceFormValidationSchema = z.object({
    professionalInsuranceLastVerify: z.date().nullish()
})
export type UserProfessionalInsuranceFormValidationSchema = z.infer<
    typeof UserProfessionalInsuranceFormValidationSchema
>

/* User absence of incompatibility form schema:
Validates the information regarding the user's absence of incompatibility, using the last verification date. */
export const UserAbsenceIncopatibilityFormValidationSchema = z.object({
    absenceOfIncompatibilityLastVerify: z.string().nullish()
})
export type UserAbsenceIncopatibilityFormValidationSchema = z.infer<
    typeof UserAbsenceIncopatibilityFormValidationSchema
>

/* User work suitable up to form schema:
Validates the user's work suitable up to date. */
export const UserWorkSuitableUpToFormValidationSchema = z.object({
    workSuitableUpTo: z.date().nullish()
})
export type UserWorkSuitableUpToFormValidationSchema = z.infer<typeof UserWorkSuitableUpToFormValidationSchema>

/* User education form schema: +
Combines various educational and professional schemas, including qualifications, ECM scores, professional register, curriculum, insurance
and absence of incompatibility details. */
export const UserEducationFormValidationSchema = z
    .object({
        qualifications: UserQualificationFormValidationSchema.array().nullish()
    })
    .and(UserProfessionalEcmScoreValidationSchema)
    .and(UserProfessionalRegisterFormValidationSchema)
    .and(UserCurriculumFormValidationSchema)
    .and(UserProfessionalInsuranceFormValidationSchema)
    .and(UserAbsenceIncopatibilityFormValidationSchema)
    .and(UserWorkSuitableUpToFormValidationSchema)
export type UserEducationFormValidationSchema = z.infer<typeof UserEducationFormValidationSchema>

/* User operator form schema:
This schema validates the user's operator-related data, such as specialization, access type, patient age range, and various evaluation attachments.
It also combines user personal data, work areas, and education schemas for comprehensive validation. */
export const UserOperatorFormSchema = z.object({
    fm07EvaluationAttachment: UploadedDocument.nullish(),
    fm07Evaluation: z.string().nullish(),
    fm08EvaluationAttachment: UploadedDocument.nullish(),
    fm08Evaluation: z.string().nullish(),
    fm08EvaluationLastUpdate: z.date().nullish(),
    specialization: z.object(UserSpecialization.shape, {required_error: i18n.t('errors:required')}),
    patientAgeRange: PatientAgeRange.nullish()
})
export type UserOperatorFormSchema = z.infer<typeof UserOperatorFormSchema>

export const UserOperatorFormValidationSchema = UserOperatorFormSchema.refine(
    values =>
        !values.specialization ||
        values.specialization.name == 'Amministrativo' ||
        (values.specialization.name && values.specialization.name != 'Amministrativo' && values.patientAgeRange),
    {
        message: i18n.t('errors:required'),
        path: ['patientAgeRange']
    }
)
    .and(UserPersonalDataFormValidationSchema)
    .and(UserWorkAreasFormValidationSchema)
    .and(UserEducationFormValidationSchema)
export type UserOperatorFormValidationSchema = z.infer<typeof UserOperatorFormValidationSchema>

/* User privacy form schema:
Validates the user's privacy-related data, such as contract type, privacy consents (B, C), FM68 consent, and the year of privacy consent. */
export const UserPrivacyFormValidationSchema = z.object({
    contractType: z.string().nullish(),
    privacyB: z.boolean().default(false),
    privacyC: z.boolean().default(false),
    fm68: z.boolean().default(false),
    privacyYear: z.string().nullish()
})
export type UserPrivacyFormValidationSchema = z.infer<typeof UserPrivacyFormValidationSchema>

/* User contractual data form schema:
This schema validates the user's contractual information, such as status, IBAN, payment method, contract type, and VAT number. It also combines user privacy details for complete contractual validation. */
export const UserContractualDataFormValidationSchema = z
    .object({
        status: z.enum(UserBase.shape['status1'].options, {required_error: i18n.t('errors:required')}),
        iban: z.string({required_error: i18n.t('errors:required')}).nullish(),
        paymentMethod: z.string({required_error: i18n.t('errors:required')}).nullish(),
        contractType: z.string({required_error: i18n.t('errors:required')}).nullish(),
        vatNumber: z.string({required_error: i18n.t('errors:required')}).nullish(),
        amount: z.string().nullish(),
        weeklyHours: z.number().nullish(),
        raExempt: z.boolean().nullish()
    })
    .refine(values => values.status != 'operative' || !values.contractType || !!values.iban, {
        message: i18n.t('errors:required'),
        path: ['iban']
    })
    .refine(values => values.status != 'operative' || !values.contractType || !!values.paymentMethod, {
        message: i18n.t('errors:required'),
        path: ['paymentMethod']
    })
    .refine(
        values =>
            values.status != 'operative' ||
            !values.contractType ||
            values.contractType == 'employee' ||
            (values.contractType && values.contractType != 'employee' && values.vatNumber),
        {
            message: i18n.t('errors:required'),
            path: ['vatNumber']
        }
    )
    .and(UserPrivacyFormValidationSchema)
export type UserContractualDataFormValidationSchema = z.infer<typeof UserContractualDataFormValidationSchema>

/* User permissions form schema:
This schema validates the permissions assigned to the user, allowing an array of permissions objects. */
export const UserPermissionsValidationSchema = z.object({
    permissions: PermissionKey.array().nullish()
})
export type UserPermissionsValidationSchema = z.infer<typeof UserPermissionsValidationSchema>

/* User access profile form schema:
Combines the role and permission schemas for the user's access profile validation, ensuring all necessary information about the user's role and permissions is provided. */
export const UserAccessProfileValidationSchema = z
    .object({
        role: Role.nullish(),
        permissionsOverwritten: z.boolean().optional()
    })
    .and(UserPermissionsValidationSchema)
export type UserAccessProfileValidationSchema = z.infer<typeof UserAccessProfileValidationSchema>

/* User job info form schema:
This schema validates the user's job-related information, such as status, start date, and candidature date.
It also includes operator, work areas, contractual data, and access profile schemas for comprehensive job validation. */

export const UserJobInfoFormSchema = z.object({
    status: z.enum(UserBase.shape['status1'].options, {required_error: i18n.t('errors:required')}),
    startDate: z
        .date({
            errorMap: issue => ({
                message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
            })
        })
        .nullish(),
    candidatureDate: z
        .date({
            errorMap: issue => ({
                message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
            })
        })
        .nullish(),
    suspensionEndDate: z
        .date({
            errorMap: issue => ({
                message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
            })
        })
        .nullish(),
    suspensionStartDate: z
        .date({
            errorMap: issue => ({
                message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
            })
        })
        .nullish(),
    suspensionReason: z.string().nullish(),
    temporaryAbsenceEndDate: z
        .date({
            errorMap: issue => ({
                message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
            })
        })
        .nullish(),
    temporaryAbsenceStartDate: z
        .date({
            errorMap: issue => ({
                message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
            })
        })
        .nullish(),
    interruptionDate: z
        .date({
            errorMap: issue => ({
                message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
            })
        })
        .nullish(),
    interruptionReason: z.enum(InterruptionReason.options, {required_error: i18n.t('errors:required')}).nullish()
})
export type UserJobInfoFormSchema = z.infer<typeof UserJobInfoFormSchema>

export const UserJobInfoFormValidationSchema = UserJobInfoFormSchema.refine(
    // operative
    values =>
        !values.status ||
        values.status == 'candidate' ||
        (values.status == 'operative' && values.startDate) ||
        (values.status == 'suspended' && values.startDate) ||
        (values.status == 'absent' && values.startDate) ||
        (values.status == 'terminated' && values.startDate),
    {
        message: i18n.t('errors:required'),
        path: ['startDate']
    }
)
    .refine(
        values =>
            // candidate
            !values.status || values.status != 'candidate' || (values.status == 'candidate' && values.candidatureDate),
        {
            message: i18n.t('errors:required'),
            path: ['candidatureDate']
        }
    )
    .refine(
        values =>
            // suspended - start date
            !values.status ||
            values.status != 'suspended' ||
            (values.status == 'suspended' && values.suspensionStartDate),
        {
            message: i18n.t('errors:required'),
            path: ['suspensionStartDate']
        }
    )
    .refine(
        values =>
            // suspended - end date
            !values.status ||
            values.status != 'suspended' ||
            (values.status == 'suspended' && values.suspensionEndDate),
        {
            message: i18n.t('errors:required'),
            path: ['suspensionEndDate']
        }
    )
    .refine(
        values =>
            // suspended - reason
            !values.status || values.status != 'suspended' || (values.status == 'suspended' && values.suspensionReason),
        {
            message: i18n.t('errors:required'),
            path: ['suspensionReason']
        }
    )
    .refine(
        values =>
            // absent - start date
            !values.status ||
            values.status != 'absent' ||
            (values.status == 'absent' && values.temporaryAbsenceStartDate),
        {
            message: i18n.t('errors:required'),
            path: ['temporaryAbsenceStartDate']
        }
    )
    .refine(
        values =>
            // absent - end date
            !values.status ||
            values.status != 'absent' ||
            (values.status == 'absent' && values.temporaryAbsenceEndDate),
        {
            message: i18n.t('errors:required'),
            path: ['temporaryAbsenceEndDate']
        }
    )
    .refine(
        values =>
            // terminated - date
            !values.status ||
            values.status != 'terminated' ||
            (values.status == 'terminated' && values.interruptionDate),
        {
            message: i18n.t('errors:required'),
            path: ['interruptionDate']
        }
    )
    .refine(
        values =>
            // terminated - reason
            !values.status ||
            values.status != 'terminated' ||
            (values.status == 'terminated' && values.interruptionReason),
        {
            message: i18n.t('errors:required'),
            path: ['interruptionReason']
        }
    )
    .and(UserOperatorFormValidationSchema)
    .and(UserWorkAreasFormValidationSchema)
    .and(UserContractualDataFormValidationSchema)
    .and(UserAccessProfileValidationSchema)
export type UserJobInfoFormValidationSchema = z.infer<typeof UserJobInfoFormValidationSchema>

/* User form schema:
This schema combines all relevant user-related schemas, including personal data, residence, domicile,
job info, contractual data, education, access profile, contacts, and privacy details.
It provides a comprehensive validation for creating or updating a user. */
export const UserFormSchema = UserPersonalDataFormValidationSchema.and(UserResidenceFormValidationSchema)
    .and(UserDomicileFormValidationSchema)
    .and(UserJobInfoFormValidationSchema)
    .and(UserContractualDataFormValidationSchema)
    .and(UserEducationFormValidationSchema)
    .and(UserAccessProfileValidationSchema)
    .and(UserPrivacyFormValidationSchema)
export type UserFormSchema = z.infer<typeof UserFormSchema>

/* Create user form schema:
Combines the user form schema and notification form schema to provide validation for creating a new user */
export const NotificationUsersConfirmationValidationSchema = z.object({
    notificationsRecipients: z.array(z.object({id: z.string().min(1), fullName: z.string()})).optional(),
    notificationDetails: z.string().optional()
})
export type NotificationUsersConfirmationValidationSchema = z.infer<
    typeof NotificationUsersConfirmationValidationSchema
>
export const CreateUserFormValidationSchema = UserFormSchema.and(NotificationUsersConfirmationValidationSchema)
export type CreateUserFormValidationSchema = z.infer<typeof CreateUserFormValidationSchema>

// Create a GroupMember schema that matches the API response for group members
export const GroupMember = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().email(),
    avatarUrl: z.string().nullable()
})
export type GroupMember = z.infer<typeof GroupMember>

// Helper function to create a GroupMember from UserSmall
export function createGroupMemberFromUser(
    user: Pick<UserSmall, 'id' | 'fullName' | 'email' | 'avatarUrl'>
): GroupMember {
    // Split fullName if firstName/lastName aren't available
    const nameParts = user.fullName ? user.fullName.split(' ') : ['', '']

    return {
        id: user.id,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: user.email,
        avatarUrl: user.avatarUrl
    }
}

export const BaseUserColumns = z.enum([
    'fullName',
    'status',
    'specializationId',
    'roleId',
    'sector',
    'patientAgeRange',
    'lastLoginAt',
    'createdAt',
    'contactEmail',
    'birthDate',
    'mobilePhone',
    'telephone'
])
export type BaseUserColumns = z.infer<typeof BaseUserColumns>

export const CustomUserColumns = z.enum([
    'taxCode',
    'startDate',
    'interruptionDate',
    'registrationDate',
    'fm08Evaluation',
    'fm08EvaluationLastUpdate',
    'curriculumLastUpdate',
    'fm68',
    'privacy',
    'professionalInsuranceLastVerify',
    'workSuitableUpTo',
    'threeYearsEcm',
    'residence',
    'domicile'
])
export type CustomUserColumns = z.infer<typeof CustomUserColumns>

export const UsersColumn = z.enum([...BaseUserColumns.options, ...CustomUserColumns.options, 'select', 'moreActions'])
export type UsersColumn = z.infer<typeof UsersColumn>

/* Columns of the operative therapists directory (`/operational-users`). No `select`, `status` or
`roleId`: the list is already scoped on active therapists and has no bulk actions. */
export const OperativeUsersColumn = z.enum([
    'fullName',
    'specializationId',
    'sector',
    'workAreas',
    'lastLoginAt',
    'createdAt',
    'contactEmail',
    'birthDate',
    'mobilePhone',
    'moreActions'
])
export type OperativeUsersColumn = z.infer<typeof OperativeUsersColumn>

/* Columns of the consultant visits table (`POST /users/:userId/events`). Mirrors
`PatientEventsColumn` with `patientId` in place of `consultantId`: the list is already scoped on the
consultant, so the counterpart shown per row is the patient. The non-`select` options are also the
`fields` accepted by the `user_events` export entity. */
export const UserEventsColumn = z.enum([
    'select',
    'date',
    'time',
    'status',
    'accessType',
    'patientId',
    'sector',
    'isRemote',
    'projectCode',
    'projectStartDate',
    'projectExpirationDate'
])
export type UserEventsColumn = z.infer<typeof UserEventsColumn>
